export interface ColumnDefinition {
  id: string;
  title: string;
  isFixed?: boolean;
}

export interface ColumnCategory {
  id: string;
  title: string;
  columns: ColumnDefinition[];
}

export interface ColumnConfig {
  categories: ColumnCategory[];
  type: string;
}

export enum ApiColumnConfigType {
  employees_columns = "employees_columns",
}

export enum EmployeeColumnId {
  employee_name = "employee_name",
  middle_name = "middle_name",
  name_pronunciation = "name_pronunciation",
  lifecycle_status = "lifecycle_status",
  last_seen = "last_seen",
  hired_on = "hired_on",
  hired_on_relative = "hired_on_relative",
  birth_date = "birth_date",
  birth_date_relative = "birth_date_relative",
  personnel_number = "personnel_number",
  gender = "gender",
  address = "address",
  address_country = "address_country",
  address_city = "address_city",
  address_state = "address_state",
  address_zip = "address_zip",
  address_line_1 = "address_line_1",
  address_line_2 = "address_line_2",
  termination_date = "termination_date",
  timezone = "timezone",

  email = "email",
  email_personal = "email_personal",
  phone = "phone",
  phone_personal = "phone_personal",
  slack = "slack",

  employee_position = "employee_position",
  job_role = "job_role",
  position_date_effective_from = "position_date_effective_from",
  department = "department",
  division = "division",
  manager = "manager",
  location = "location",
  legal_entity = "legal_entity",
  department_manager = "department_manager",
  employee_position_change_reason = "employee_position_change_reason",
  position_comment = "position_comment",

  contract = "contract",
  employment_type = "employment_type",
  employment_date_effective_from = "employment_date_effective_from",
  employment_date_effective_to = "employment_date_effective_to",
  termination_reason = "termination_reason",
  last_working_date = "last_working_date",
  termination_leave_type = "termination_leave_type",
  replacement_employee = "replacement_employee",
  eligible_for_rehire = "eligible_for_rehire",
  probation_period_end = "probation_period_end",
  work_pattern = "work_pattern",
  employment_comment = "employment_comment",

  base_salary_gross = "base_salary_gross",
  base_salary_net = "base_salary_net",
  compensation_total_cost = "compensation_total_cost",
  compensation_per_type = "compensation_per_type",
  compensation_change_reason = "compensation_change_reason",
  compensation_date_effective_from = "compensation_date_effective_from",
  compensation_currency = "compensation_currency",
  compensation_comment = "compensation_comment",
}

const employeeSearchColumnKeys: Partial<Record<EmployeeColumnId, string>> = {
  [EmployeeColumnId.middle_name]: "employee.middle_name",
  [EmployeeColumnId.name_pronunciation]: "employee.name_pronunciation",
  [EmployeeColumnId.lifecycle_status]: "employee.lifecycle_status",
  [EmployeeColumnId.last_seen]: "employee.last_seen",
  [EmployeeColumnId.hired_on]: "employee.hired_on",
  [EmployeeColumnId.hired_on_relative]: "employee.hired_on",
  [EmployeeColumnId.birth_date]: "employee.birth_date",
  [EmployeeColumnId.birth_date_relative]: "employee.birth_date",
  [EmployeeColumnId.personnel_number]: "employee.personnel_number",
  [EmployeeColumnId.gender]: "employee.gender",
  [EmployeeColumnId.address]: "employee.address",
  [EmployeeColumnId.address_country]: "employee.address_country",
  [EmployeeColumnId.address_city]: "employee.address_city",
  [EmployeeColumnId.address_state]: "employee.address_state",
  [EmployeeColumnId.address_zip]: "employee.address_zip",
  [EmployeeColumnId.address_line_1]: "employee.address_line_1",
  [EmployeeColumnId.address_line_2]: "employee.address_line_2",
  [EmployeeColumnId.termination_date]: "employee.termination.date",
  [EmployeeColumnId.timezone]: "employee.timezone",
  [EmployeeColumnId.email]: "employee.email",
  [EmployeeColumnId.email_personal]: "employee.email_personal",
  [EmployeeColumnId.phone]: "employee.phone",
  [EmployeeColumnId.phone_personal]: "employee.phone_personal",
  [EmployeeColumnId.slack]: "employee.slack",
  [EmployeeColumnId.employee_position]: "employee.position.job_profile",
  [EmployeeColumnId.job_role]: "employee.position.job_role",
  [EmployeeColumnId.position_date_effective_from]: "employee.position.effective_from",
  [EmployeeColumnId.department]: "employee.position.department",
  [EmployeeColumnId.division]: "employee.position.division",
  [EmployeeColumnId.manager]: "employee.position.manager",
  [EmployeeColumnId.location]: "employee.position.location",
  [EmployeeColumnId.legal_entity]: "employee.position.legal_entity",
  [EmployeeColumnId.department_manager]: "employee.position.department_manager",
  [EmployeeColumnId.employee_position_change_reason]: "employee.position.change_reason",
  [EmployeeColumnId.contract]: "employee.employment.contract",
  [EmployeeColumnId.employment_type]: "employee.employment.type",
  [EmployeeColumnId.employment_date_effective_from]: "employee.employment.effective_from",
  [EmployeeColumnId.employment_date_effective_to]: "employee.employment.effective_to",
  [EmployeeColumnId.termination_reason]: "employee.termination.reason",
  [EmployeeColumnId.last_working_date]: "employee.termination.last_working_date",
  [EmployeeColumnId.termination_leave_type]: "employee.termination.leave_type",
  [EmployeeColumnId.replacement_employee]: "employee.termination.replacement_employee",
  [EmployeeColumnId.eligible_for_rehire]: "employee.termination.eligible_for_rehire",
  [EmployeeColumnId.probation_period_end]: "employee.employment.probation_period_end",
  [EmployeeColumnId.work_pattern]: "employee.employment.work_pattern",
  [EmployeeColumnId.base_salary_gross]: "employee.compensation.base_salary_gross",
  [EmployeeColumnId.base_salary_net]: "employee.compensation.base_salary_net",
  // The backend catalog has no total-cost column; any compensation column loads the rollups.
  [EmployeeColumnId.compensation_total_cost]: "employee.compensation.base_salary_gross",
  [EmployeeColumnId.compensation_per_type]: "employee.compensation.per_type",
  [EmployeeColumnId.compensation_change_reason]: "employee.compensation.change_reason",
  [EmployeeColumnId.compensation_date_effective_from]: "employee.compensation.effective_from",
};

const employeeColumnIds = new Set<string>(Object.values(EmployeeColumnId));

export const getEmployeeSearchLoadColumns = (columns: string[]): string[] =>
  Array.from(
    new Set(
      columns.flatMap((column) => {
        if (column.startsWith("employee.")) return [column];

        const systemColumn = employeeSearchColumnKeys[column as EmployeeColumnId];
        if (systemColumn) return [systemColumn];

        return employeeColumnIds.has(column) ? [] : [`employee.custom_field.${column}`];
      })
    )
  );
